import { ref } from "vue";

import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICustomerOrder, IOrderPaymentConfirmation } from "@/data/model/Customer";
import type { ICustomerPayment } from "@/data/model/CustomerPayment";
import { PaymentFor } from "@/data/model/Enums";
import { shouldShowReceiptActionsForOperator } from "@/data/services/PaymentService";
import { exceptionTypes } from "@/data/util/Config";
import { Format } from "@/data/util/Format";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { logInformation } from "@/shared/logging/informations";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { Ripple } from "@/shared/ui/Ripple";
import { createVueComponent } from "@/shared/utils/vue";
import { createEmptyDiv } from "@/shared/utils/vue";

import AppReceiptActionButtons from "@/components/payment/AppReceiptActionButtons.vue";

import { appEvents } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { ViewTransition } from "@/app/controller/BaseController";
import { BaseController } from "@/app/controller/BaseController";
import { EmailInput } from "@/app/elements/EmailInput";
import { PasswordInput } from "@/app/elements/PasswordInput";
import { Toggle } from "@/app/elements/Toggle";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { OrderReceivedPopover } from "@/app/popover/OrderReceivedPopover";
import { PaymentReceivedPopover } from "@/app/popover/PaymentReceivedPopover";
import { TextPopover } from "@/app/popover/TextPopover";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";
import { UserMenu } from "@/app/ui/UserMenu";

export interface IDoneControllerTransitionData {
    transactionId?: string;
    externalPaymentId?: string;
    confirmation?: IOrderPaymentConfirmation;
}

export class DoneController extends BaseController<IDoneControllerTransitionData> {
    public onStartOver!: { () };
    public onPopoverBecameVisible?: { () };

    private $head?: JQuery;
    private emailInput?: EmailInput;
    private $headLine?: JQuery;
    private $state?: JQuery;
    private $signupForm?: JQuery;
    private $passwordRow?: JQuery;
    private passwordInput?: PasswordInput;
    private $firstNameRow?: JQuery;
    private $firstNameInput?: JQuery;
    private $lastNameRow?: JQuery;
    private $lastNameInput?: JQuery;
    private onlyReceiptToggle?: Toggle;
    private $socialLogins?: JQuery;
    private $disclaimer?: JQuery;
    private $displayOrder?: JQuery;
    private $mainContainer?: JQuery;
    private $onlyReceiptSection?: JQuery; // only receipt section is shown only if payment was not pay later and user is not waiter
    private $paymentState?: JQuery;

    private cancelLongpoll = false;
    private transactionId?: string;
    private externalPaymentId?: string;
    private confirmation?: IOrderPaymentConfirmation;
    public userMenu?: UserMenu;
    private currentOffset = 0;
    private entriesPerPage = 1;
    private wasPayLater?: boolean;
    private emailRef = ref<string | undefined>();

    private $doneBtn?: JQuery;
    private paymentFor?: PaymentFor;

    public $popover?: PaymentReceivedPopover | OrderReceivedPopover;

    private customerDao: CustomerDao;

    constructor() {
        super("done");
        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    }

    protected onBecomeVisible(data?: IDoneControllerTransitionData) {
        if (data == null) {
            return;
        }

        this.cancelLongpoll = false;

        // we provide either transactionId, externalPaymentId or confirmation
        if (data.transactionId != null) {
            this.transactionId = data.transactionId;
            this.confirmation = undefined;
            this.setValidatingTransaction();
            return;
        }

        if (data.externalPaymentId != null) {
            this.externalPaymentId = data.externalPaymentId;
            this.confirmation = undefined;
            this.setValidatingTransaction();
            return;
        }

        // we provide confirmation
        const confirmation = data.confirmation!;
        this.transactionId = undefined;
        this.confirmation = confirmation;
        this.wasPayLater = confirmation.isPayLater;
        this.paymentFor = confirmation.paymentFor;

        if (confirmation.isConfirmed === true) {
            logInformation("Show confirmation popover #2");
            this.showConfirmationPopover({ confirmation, focusEmailInput: true });

            this.setSuccess(confirmation);
        } else if (confirmation.isConfirmed === false) {
            this.setFailed(confirmation.error);
        }
    }

    private showConfirmationPopover(options: { confirmation: IOrderPaymentConfirmation; focusEmailInput?: boolean }) {
        // rewrite values to variables before disposing context
        const confirmation = options.confirmation;
        const isWaiterOrAdmin = TastierClient.identityContext.isWaiterOrAdmin();
        const allocation = TastierClient.context.currentAllocation;
        const useCase = Helper.getUseCaseByOrderOrOfferServiceId(options.confirmation.orderServiceId);

        const showReceiptActionsForOperator = shouldShowReceiptActionsForOperator({
            confirmation: confirmation,
            isWaiter: isWaiterOrAdmin,
            orderServices: Helper.getAvailableOrderOrOfferServices(),
            paymentServices: Helper.getAvailablePaymentServices(),
        });

        // NOTE - this part disposes DoneController and app context, e.g. allocation is refreshed
        // we need to save required values before disposing context
        if (isWaiterOrAdmin && !showReceiptActionsForOperator && !options.confirmation.isPayLater) {
            if (this.onStartOver) {
                this.onStartOver();
            }
        }

        setTimeout(() => {
            const uniqueId = confirmation.orderId ?? confirmation.paymentId;
            if (uniqueId != null && uniqueId.length > 0 && BasePopover.hasCurrentlyVisiblePopover(uniqueId)) {
                logInformation("The popover " + uniqueId + "is already visible");
                return;
            }

            const popover =
                confirmation.paymentFor === PaymentFor.Bill
                    ? new PaymentReceivedPopover({
                          confirmation,
                          isWaiterOrAdmin,
                          showReceiptActionsForOperator,
                          uniqueId,
                      })
                    : new OrderReceivedPopover({
                          confirmation,
                          isWaiterOrAdmin,
                          allocation,
                          orderUseCase: useCase,
                          showReceiptActionsForOperator,
                          uniqueId,
                      });

            popover.onBecameVisible = this.onPopoverBecameVisible;
            popover.onStartOver = this.onStartOver;
            popover.onReceiptAndMore = () => {
                if (options.focusEmailInput && this.userIsWaiter()) {
                    this.emailInput!.$input.focus();
                }
            };
            popover.show();
            this.$popover = popover;
        }, 400);
    }

    public refreshPopoverUseCaseSection() {
        if (this.$popover != null) {
            const orderReceivedPopover = this.$popover as OrderReceivedPopover;
            orderReceivedPopover.refreshUseCaseSection();
        }
    }

    protected onBecomeInvisible() {
        this.cancelLongpoll = true;
        this.wasPayLater = false;
    }

    private async setValidatingTransaction() {
        this.$state = $('<div class="state" />').appendTo(this.$view.empty());
        this.$state.append('<div class="circleloader"></div>');
        this.$state.append(
            '<div class="sub"><div class="checking">' +
                trans.done.trxCheckingTitle +
                '</div><div class="holdon">' +
                trans.done.trxCheckingText +
                "</div></div>"
        );

        try {
            let result: IOrderPaymentConfirmation;
            if (this.transactionId != null) {
                result = await this.longpollTransaction(this.transactionId!);
            } else if (this.externalPaymentId != null) {
                result = await this.longpollTransactionByExternalPayment(this.externalPaymentId!);
            } else {
                throw new Error("Transaction id and external payment id are missing");
            }

            this.wasPayLater = result.isPayLater;
            this.paymentFor = result.paymentFor;

            if (result.isConfirmed === false) {
                const loader = this.$state!.find("> .circleloader").append(
                    '<div class="failmark"><i class="fal fa-times"></i></div>'
                );
                loader[0].offsetHeight;
                loader.addClass("fail");

                this.$state!.find("> .sub").html(
                    '<div class="checking">' +
                        trans.done.trxDeclinedTitle +
                        '</div><div class="holdon">' +
                        (result.error !== undefined ? Format.endWithFullstop(result.error) + " " : "") +
                        trans.done.trxDeclinedText +
                        "</div>"
                );

                this.setFailed(result.error);
            } else {
                const loader = this.$state!.find("> .circleloader").append('<div class="checkmark draw"></div>');
                loader[0].offsetHeight;
                loader.addClass("success");

                this.$state!.find("> .sub").html('<div class="checking">' + trans.done.trxSuccessTitle + "</div>");

                logInformation("Show confirmation popover #1");
                this.showConfirmationPopover({ confirmation: result });

                setTimeout(() => {
                    this.$state!.fadeOut(400, () => {
                        this.setSuccess(result);
                    });
                }, 1000);
            }
        } catch (ex) {
            logError(ex);
            const loader = this.$state!.find("> .circleloader").append(
                '<div class="failmark"><i class="fal fa-times"></i></div>'
            );
            loader[0].offsetHeight;
            loader.addClass("fail");

            this.$state!.find("> .sub").html(
                '<div class="checking">' +
                    trans.done.trxFailedCheckingTitle +
                    '</div><div class="holdon">' +
                    trans.done.trxFailedCheckingText +
                    "</div>"
            );
            this.setFailed();
        }
    }

    private setSuccess(confirmation: IOrderPaymentConfirmation) {
        const profile = TastierClient.identityContext.getProfile()!;
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();

        this.confirmation = confirmation;
        this.$mainContainer = $("#tastierapp");
        this.$view.empty();
        this.$head = $(
            '<div class="head"><div class="title">' +
                (this.wasPayLater ? trans.done.more : trans.done.title) +
                "</div></div>"
        ).appendTo(this.$view);
        this.userMenu = new UserMenu().appendTo(this.$head);
        const scrollable = $('<div class="scrollable" />').hide().appendTo(this.$view);

        this.$headLine = $('<div class="headline"></div>').appendTo(scrollable);

        if (isAuthenticated) {
            if (this.wasPayLater) {
                const hasIdentityEmail = TastierClient.identityContext.hasIdentityEmail();
                if (!hasIdentityEmail || !confirmation.sendEmailConfirmationsForUnpaidOrders) {
                    this.$headLine.hide();
                }
            } else {
                this.$headLine.text(trans.done.getReceipt);
            }
        } else {
            if (this.wasPayLater) {
                this.$headLine.text(trans.done.signup);
            } else {
                this.$headLine.text(trans.done.onlyReceipt);
            }
        }

        this.$signupForm = $('<form class="form" />').appendTo(scrollable);
        const row = $('<div class="receipt-actions formrow" />').appendTo(this.$signupForm);
        this.emailInput = new EmailInput().appendTo(row);
        this.emailRef.value = this.emailInput.getValue();
        this.renderReceiptActionButtons(confirmation.paymentFor, confirmation.orderId, confirmation.paymentId, row);

        this.emailInput.onKeyUp(() => {
            this.checkSignupReceiptState();

            const emailVal = this.emailInput!.getValue();
            this.emailRef.value = emailVal;
        });

        this.$passwordRow = $('<div class="signuppw formrow" />').appendTo(this.$signupForm);
        this.passwordInput = new PasswordInput(true, false, trans.signin.password).appendTo(this.$passwordRow);
        this.passwordInput.onKeyUp(() => {
            this.checkSignupReceiptState();
        });

        this.$firstNameRow = $('<div class="formrow signupfname" />').appendTo(this.$signupForm);
        this.$firstNameInput = $(
            '<div class="formcontrol"><label>' +
                trans.signup.firstName +
                '</label><input type="text" name="firstname" value="' +
                (profile !== undefined && profile.firstName !== undefined ? profile.firstName : "") +
                '" /></div>'
        )
            .appendTo(this.$firstNameRow)
            .find("> input");

        this.$lastNameRow = $('<div class="formrow signuplname" />').appendTo(this.$signupForm);
        this.$lastNameInput = $(
            '<div class="formcontrol"><label>' +
                trans.signup.lastName +
                '</label><input type="text" name="lastname" value="' +
                (profile !== undefined && profile.lastName !== undefined ? profile.lastName : "") +
                '" /></div>'
        )
            .appendTo(this.$lastNameRow)
            .find("> input");

        this.$onlyReceiptSection = $('<div class="onlyrecp formrow">' + trans.done.onlyReceipt + "</div>").appendTo(
            this.$signupForm
        );

        this.onlyReceiptToggle = new Toggle().appendTo(this.$onlyReceiptSection);

        if (!this.wasPayLater) {
            this.onlyReceiptToggle!.onChange = () => this.checkSignupReceiptState();
            this.$onlyReceiptSection.show();
        } else {
            this.$onlyReceiptSection.hide();
        }

        this.$socialLogins = $('<div class="sociallogins" />').appendTo(scrollable);
        Ripple.bindRippleOut($('<div class="sociallogin fb"><i class="fab fa-facebook-f"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuthAndSend("facebook");
            });

        Ripple.bindRippleOut($('<div class="sociallogin linkedin"><i class="fab fa-linkedin-in"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuthAndSend("linkedin");
            });

        Ripple.bindRippleOut($('<div class="sociallogin google"><i class="fab fa-google"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuthAndSend("google");
            });

        this.$disclaimer = $('<div class="disclaimer">' + trans.done.disclaimer + "</div>").appendTo(scrollable);
        this.$displayOrder = $('<div class="order"></div>').appendTo(scrollable);

        if (confirmation.paymentFor !== PaymentFor.Bill) {
            $('<div class="headline">' + trans.done.displayOrderHeadline + "</div>").appendTo(this.$displayOrder);

            Ripple.bind(
                $('<button class="btn" type="button"> ' + trans.done.displayOrderBtn + " </button>").click(() => {
                    this.fetchLastOrder();
                })
            ).appendTo(this.$displayOrder);
        } else {
            $('<div class="headline">' + trans.done.displayPaymentHeadline + "</div>").appendTo(this.$displayOrder);

            Ripple.bind(
                $('<button class="btn" type="button"> ' + trans.done.displayPaymentBtn + " </button>").click(() => {
                    this.fetchCustomerPayment(this.confirmation!.paymentId!);
                })
            ).appendTo(this.$displayOrder);
        }

        FloatLabel.bind(this.$signupForm);

        const bottom = $('<div class="bottom" />').hide().appendTo(this.$view);
        this.$paymentState = $('<div class="state"></div>').appendTo(bottom);

        if (!confirmation.isPayLater) {
            this.updatePaymentStateText(confirmation.paymentFor);
        }

        this.$doneBtn = Ripple.bind($('<button class="submit btn" type="button" />')).appendTo(bottom);
        this.setDoneButtonEnabled(false);

        if (this.userIsWaiter()) {
            this.emailInput.setValue("");
            this.emailRef.value = this.emailInput.getValue();
            this.onlyReceiptToggle.setValue(true);
            this.$onlyReceiptSection.hide();

            if (!this.wasPayLater) {
                const continueBtn = Ripple.bind($('<button class="submit btn" type="button" />')).appendTo(bottom);
                continueBtn.html(trans.done.startOver).bind("click.b", () => {
                    this.goBackToWelcomeScreen();
                });
            }
        }

        this.setInitialState();

        scrollable.fadeIn(1500);
        bottom.fadeIn(1500);
    }

    private async updatePaymentStateText(paymentFor: PaymentFor) {
        if (paymentFor === PaymentFor.Bill) {
            const billId = TastierClient.context.currentBill?.id;

            try {
                const f = await this.customerDao.getCurrentBill({ billId: billId });
                if (f == null || f.unpaid === 0) {
                    this.$paymentState!.text(trans.done.fullyPaid);
                } else {
                    this.$paymentState!.html(trans.done.unpaid + " <b>CHF " + f.unpaid.toFixed(2) + "</b>");
                }
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                // All orders have been fully paid, so the bill has been closed
                if (e.type === exceptionTypes.billDoesNotExistException) {
                    this.$paymentState!.text(trans.done.fullyPaid);
                }
            }
        } else {
            this.$paymentState!.text(trans.done.fullyPaid);
        }
    }

    private setFailed(error?: string) {
        if (this.$state != null) {
            this.$state.remove();
            this.$state = undefined;
        }

        this.$state = $('<div class="state" />').appendTo(this.$view.empty());
        this.$state.append(
            '<div class="circleloader fail"><div class="failmark"><i class="fal fa-times"></i></div></div>'
        );
        this.$state.append(
            '<div class="sub"><div class="checking">' +
                trans.done.trxDeclinedTitle +
                '</div><div class="holdon">' +
                (error !== undefined ? Format.endWithFullstop(error) + " " : "") +
                trans.done.trxDeclinedText +
                "</div></div>"
        );

        const bottom = $('<div class="bottom" />').hide().appendTo(this.$view);
        this.$doneBtn = Ripple.bind(
            $(
                '<button class="submit btn" type="button">' +
                    (this.paymentFor === PaymentFor.Bill ? trans.done.backToPayment : trans.done.backToOrder) +
                    "</div>"
            ).click(() => {
                this.goBackToWelcomeScreen();
            })
        ).appendTo(bottom);

        bottom.fadeIn(1500);
    }

    private longpollTransactionByExternalPayment(externalPaymentId: string): Promise<IOrderPaymentConfirmation> {
        return new Promise((resolve, reject) => {
            let retries = 0;

            const checkRecursive = async (externalPaymentId: string) => {
                if (this.cancelLongpoll) return;

                try {
                    const result =
                        await this.customerDao.checkOrderPaymentConfirmationByExternalPaymentId(externalPaymentId);

                    if (retries > 50) {
                        reject();
                        return;
                    }

                    if (result.isConfirmed === undefined) {
                        retries++;

                        const delay = retries <= 1 ? 500 : retries <= 5 ? 2500 : 6000;
                        window.setTimeout(() => checkRecursive(externalPaymentId), delay);
                    } else {
                        resolve(result);
                    }
                } catch (ex) {
                    logError(ex);
                    reject(ex);
                }
            };

            checkRecursive(externalPaymentId);
        });
    }

    private longpollTransaction(transactionId: string): Promise<IOrderPaymentConfirmation> {
        return new Promise((resolve, reject) => {
            let retries = 0;

            const checkRecursive = async (transactionId: string) => {
                if (this.cancelLongpoll) return;

                try {
                    const result = await this.customerDao.checkOrderPaymentConfirmation(transactionId);

                    if (retries > 50) {
                        reject();
                        return;
                    }

                    if (result.isConfirmed === undefined) {
                        retries++;

                        const delay = retries <= 1 ? 500 : retries <= 5 ? 2500 : 6000;
                        window.setTimeout(() => checkRecursive(transactionId), delay);
                    } else {
                        resolve(result);
                    }
                } catch (ex) {
                    logError(ex);
                    reject(ex);
                }
            };

            checkRecursive(transactionId);
        });
    }

    private setInitialState() {
        const profile = TastierClient.identityContext.getProfile()!;
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();
        // if an identity has an email, that means receipts and confirmations are sent automatically
        const hasIdentityEmail = TastierClient.identityContext.hasIdentityEmail();
        const useCase = Helper.getCurrentOrderServiceUseCase();
        const sendEmailConfirmationsForUnpaidOrders = useCase?.sendEmailConfirmationsForUnpaidOrders ?? false;
        const isWaiter = this.userIsWaiter();

        if (!this.wasPayLater && isWaiter) {
            this.$doneBtn!.hide();
        } else {
            this.$doneBtn!.show();
        }

        this.$displayOrder!.removeClass("hidden");
        if (isAuthenticated || (hasIdentityEmail && !this.wasPayLater)) {
            this.$signupForm!.addClass("hidden");
            this.$socialLogins!.addClass("hidden");
            this.$disclaimer!.addClass("hidden");

            if (isWaiter) {
                if (!this.wasPayLater) {
                    this.$signupForm!.removeClass("hidden");
                } else {
                    this.$headLine!.hide();
                }
            } else {
                if (!this.wasPayLater) {
                    this.$headLine!.html(trans.done.weSentReceipt + " " + Format.htmlEscape(profile.email));
                } else {
                    if (hasIdentityEmail && sendEmailConfirmationsForUnpaidOrders) {
                        this.$headLine!.html(
                            `<div class="identity-email">${trans.done.weSentConfirmation} ${Format.htmlEscape(profile.email)}</div>`
                        );
                    } else {
                        this.$headLine!.hide();
                    }
                }
            }
        } else {
            if (hasIdentityEmail && this.wasPayLater && sendEmailConfirmationsForUnpaidOrders) {
                this.$headLine!.html(
                    `<div class="identity-email">${trans.done.weSentConfirmation} ${Format.htmlEscape(profile.email)}</div>`
                );
                $(`<div>${this.wasPayLater ? trans.done.signup : trans.done.signupAndReceipt}</div>`).appendTo(
                    this.$headLine!
                );
            } else {
                this.$headLine!.html(this.wasPayLater ? trans.done.signup : trans.done.signupAndReceipt);
            }

            if (!this.wasPayLater) {
                $('<div class="tip"><i class="fas fa-question-circle"></i></div>')
                    .appendTo(this.$headLine!)
                    .click(() => {
                        TextPopover.showFor(trans.done.signupHintTitle, trans.done.signupHintText);
                    });
            }

            this.$signupForm!.removeClass("hidden");
            this.$socialLogins!.removeClass("hidden");
            this.$disclaimer!.removeClass("hidden");
        }

        this.checkSignupReceiptState();
    }

    private checkSignupReceiptState() {
        const isOnlyReceipt = this.onlyReceiptToggle!.getValue();
        const hasEmailVal = this.emailInput!.getValue();
        const isCurrentlyVisible = this.$passwordRow!.is(":visible");
        const shouldBeVisible = hasEmailVal !== undefined && !isOnlyReceipt;

        const socialsVisible = this.$socialLogins!.is(":visible");
        if (socialsVisible && isOnlyReceipt) {
            this.$socialLogins!.stop(true, false).slideUp("fast", "easeOutExpo");
            this.$disclaimer!.stop(true, false).slideUp("fast", "easeOutExpo");
        } else if (!socialsVisible && !isOnlyReceipt) {
            this.$socialLogins!.stop(true, false).slideDown("fast", "easeOutExpo");
            this.$disclaimer!.stop(true, false).slideDown("fast", "easeOutExpo");
        }

        if (isCurrentlyVisible && !shouldBeVisible) {
            this.$passwordRow!.stop(true, false).slideUp("fast", "easeOutExpo");
            this.$firstNameRow!.stop(true, false).slideUp("fast", "easeOutExpo");
            this.$lastNameRow!.stop(true, false).slideUp("fast", "easeOutExpo");
        } else if (!isCurrentlyVisible && shouldBeVisible) {
            this.$passwordRow!.stop(true, false).slideDown("fast", "easeOutExpo");
            this.$firstNameRow!.stop(true, false).slideDown("fast", "easeOutExpo");
            this.$lastNameRow!.stop(true, false).slideDown("fast", "easeOutExpo");
        }

        if (hasEmailVal) {
            this.$displayOrder!.addClass("hidden");
        } else {
            this.$displayOrder!.removeClass("hidden");
        }

        this.checkButton();
    }

    public checkButton() {
        const email = this.emailInput!.getValue();

        const isAuthenticated = TastierClient.identityContext.isAuthenticated();
        const isEmailFilled = email !== undefined;
        const isReceipt = this.onlyReceiptToggle!.getValue();
        const isWaiter = this.userIsWaiter();
        const isPayLater = this.wasPayLater;

        const isSignup = !isReceipt && isEmailFilled;

        const isEndUserPayImmediatelly = !isAuthenticated && !isPayLater && isReceipt;
        const isEndUserSignup = !isAuthenticated && isSignup;

        if (isPayLater && isWaiter && isReceipt) {
            this.emailInput!.$input.hide();
        }

        if (isEndUserPayImmediatelly) {
            // done button works as "Send Receipt" button
            this.updateDoneButton(this.$doneBtn!, translations.receipt.sendReceipt, () => {
                this.sendReceipt(this.confirmation!);
            });
            const isEnabled = this.emailInput!.isValid();
            this.setDoneButtonEnabled(isEnabled);
            return;
        }

        if (isEndUserSignup) {
            // done button works as "Signup" button or "Signup and Send Receipt" button
            this.updateDoneButton(
                this.$doneBtn!,
                isPayLater ? trans.done.signup : trans.done.signupAndReceiptBtn,
                () => {
                    this.signupAndSend();
                }
            );
            const isEnabled = this.emailInput!.isValid() && this.passwordInput!.isValid();
            this.setDoneButtonEnabled(isEnabled);
            return;
        }
        // done button works as "Continue" button
        this.updateDoneButton(this.$doneBtn!, trans.done.startOver, () => {
            this.goBackToWelcomeScreen();
        });
        this.setDoneButtonEnabled(true);
    }

    private async signupAndSend() {
        if (this.$doneBtn!.hasClass("loading")) return;

        const profile = TastierClient.identityContext.getProfile()!;
        const fName =
            this.$firstNameInput!.val().length === 0
                ? profile.firstName === undefined
                    ? "Unknown"
                    : profile.firstName
                : this.$firstNameInput!.val();
        const lName =
            this.$lastNameInput!.val().length === 0
                ? profile.lastName === undefined
                    ? "User"
                    : profile.lastName
                : this.$lastNameInput!.val();

        this.emailInput!.setError(false);

        try {
            this.setDoneButtonLoading(true);
            await TastierClient.identityContext.signup({
                email: this.emailInput!.getValue(),
                firstName: fName,
                lastName: lName,
                password: this.passwordInput!.getValue(),
                phoneNumber: profile.phoneNumber,
            });

            if (this.wasPayLater) {
                this.setDoneButtonLoading(false);
                this.goBackToWelcomeScreen();
            } else {
                await this.sendReceipt(this.confirmation!);
            }
        } catch (ex) {
            logError(ex);
            const e = ex as DaoError;
            if (e.type === exceptionTypes.emailInUseException) {
                this.emailInput!.setError(true);
                Popup.show(trans.signup.emailAlreadyUsed);
            } else {
                Popup.show(trans.done.failedSignup);
            }

            this.setDoneButtonLoading(false);
        }
    }

    private async externalAuthAndSend(provider: string) {
        const c = this.confirmation; //create scope copy because this will get reset after view is disappeared

        try {
            this.setDoneButtonLoading(true);
            await TastierClient.identityContext.authExternal(provider);

            if (this.wasPayLater) {
                this.setDoneButtonLoading(false);
                this.goBackToWelcomeScreen();
            } else {
                await this.sendReceipt(c!);
            }
        } catch (ex) {
            logError(ex);
            Popup.show(trans.done.failedSignup);
            this.setDoneButtonLoading(false);
        }
    }

    private async sendReceipt(confirmation: IOrderPaymentConfirmation) {
        if (confirmation === undefined || confirmation.isConfirmed !== true) {
            this.goBackToWelcomeScreen();
            return;
        }

        switch (confirmation.paymentFor) {
            case PaymentFor.Order:
                await this.sendOrderPaymentReceipt(confirmation, this.emailInput!.getValue());
                break;
            case PaymentFor.Bill:
                await this.sendBillPaymentReceipt(confirmation, this.emailInput!.getValue());
                break;
        }
    }

    private async sendOrderPaymentReceipt(confirmation: IOrderPaymentConfirmation, toReceiver?: string) {
        try {
            this.setDoneButtonLoading(true);
            await this.customerDao.sendReceipt(confirmation.orderId!, {
                email: toReceiver!,
            });

            Popup.show(translations.receipt.sentReceiptTo.replace("{0}", toReceiver!.toLowerCase()), () => {
                this.goBackToWelcomeScreen();
            });
        } catch (ex) {
            logError(ex);
            Popup.show(translations.receipt.failedSendReceipt);
        } finally {
            this.setDoneButtonLoading(false);
        }
    }

    private async sendBillPaymentReceipt(confirmation: IOrderPaymentConfirmation, toReceiver?: string) {
        try {
            this.setDoneButtonLoading(true);
            await this.customerDao.sendBillReceipt(confirmation.paymentId!, {
                email: toReceiver!,
            });

            Popup.show(translations.receipt.sentReceiptTo.replace("{0}", toReceiver!.toLowerCase()), () => {
                this.goBackToWelcomeScreen();
            });
        } catch (ex) {
            logError(ex);
            Popup.show(translations.receipt.failedSendReceipt);
        } finally {
            this.setDoneButtonLoading(false);
        }
    }

    private async fetchLastOrder() {
        try {
            const c = await this.customerDao.getOrders({
                offset: this.currentOffset,
                limit: this.entriesPerPage,
            });

            const totalItems = this.currentOffset + c.items.length;
            if (totalItems !== 0) {
                this.showReceipt(c.items[0]);
            }
        } catch (ex) {
            logError(ex);
        }
    }

    private async fetchCustomerPayment(paymentId: string) {
        try {
            const c = await this.customerDao.getCustomerPayment(paymentId);
            this.showBillReceipt(c);
        } catch (ex) {
            logError(ex);
        }
    }

    private async showReceipt(data: ICustomerOrder) {
        const showReceiptData = {
            order: data,
            transition: ViewTransition.SlideLeft,
            onGoBack: () => this.show(ViewTransition.SlideRight),
        };
        appEvents.OnShowReceipt.dispatch(showReceiptData);
    }

    private showBillReceipt(data: ICustomerPayment) {
        const showPaymentReceiptData = {
            payment: data,
            transition: ViewTransition.SlideLeft,
            onGoBack: () => this.show(ViewTransition.SlideRight),
        };
        appEvents.OnShowPaymentReceipt.dispatch(showPaymentReceiptData);
    }

    private userIsWaiter(): boolean {
        return TastierClient.identityContext?.isWaiterOrAdmin();
    }

    private renderReceiptActionButtons(
        paymentFor: PaymentFor,
        orderId: string | undefined,
        paymentId: string | undefined,
        receiptButtonsContainer: JQuery
    ) {
        // receipt action buttons should be shown only if user is waiter and payment was not pay later
        if ((this.userIsWaiter() && !this.wasPayLater) == false) {
            return;
        }

        const div = createEmptyDiv();
        const props = {
            paymentFor: paymentFor,
            emailRecipient: this.emailRef,
            orderId: orderId,
            paymentId: paymentId,
            locationId: TastierClient.context.currentAllocation!.locationId,
            userIsWaiter: this.userIsWaiter(),
            isPayLater: this.wasPayLater,
            onSendReceiptSuccess: () => this.goBackToWelcomeScreen(),
            onPrintSuccess: () => this.goBackToWelcomeScreen(),
        };
        createVueComponent(AppReceiptActionButtons, props, div);
        $(div).appendTo(receiptButtonsContainer);
    }

    private goBackToWelcomeScreen() {
        if (this.onStartOver) {
            this.onStartOver();
        }
    }

    private updateDoneButton(button: JQuery, text: string, clickHandler: () => void) {
        button
            ?.html(text)
            .unbind("click.b")
            .bind("click.b", () => {
                clickHandler();
            });
    }

    private setDoneButtonEnabled(enable: boolean) {
        if (enable) {
            // enable with delay
            setTimeout(() => {
                this.$doneBtn!.prop("disabled", false).removeClass("disabled");
            }, 700);
        } else {
            this.$doneBtn!.prop("disabled", true).addClass("disabled");
        }
    }

    private setDoneButtonLoading(isLoading: boolean) {
        if (isLoading) {
            this.$doneBtn!.addClass("loading");
        } else {
            this.$doneBtn!.removeClass("loading");
        }
    }
}
